import { memo, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { type InlineInputProps } from '@air/component-inline-input';
import {
  contentCollectionClipsInvalidFieldsSelector,
  contentCollectionRequiredFieldsSelector,
} from '@air/feature-content-collection/redux';
import { Label } from '@air/primitive-label';
import { cn } from '@air/utils-tailwind';

import { type ContentCollectionBaseCustomFieldsProps } from '~/components/ContentCollection/ContentCollectionBaseCustomFieldsSelect';
import {
  ResizableEditableTextInput,
  type ResizableEditableTextInputProps,
} from '~/components/ResizableEditableTextInput';
import { type AirState } from '~/store/types';

export type ContentCollectionBaseCustomFieldPlainTextProps = {
  onTextChange: (params: string) => void;
} & ContentCollectionBaseCustomFieldsProps &
  Pick<ResizableEditableTextInputProps, 'size'>;

export const ContentCollectionBaseCustomFieldPlainText = memo(
  ({
    disabled = false,
    field,
    onTextChange,
    clips,
    size = 'medium',
    showDescription = false,
  }: ContentCollectionBaseCustomFieldPlainTextProps) => {
    const requiredFields = useSelector(contentCollectionRequiredFieldsSelector);
    const invalidFields = useSelector((st: AirState) =>
      contentCollectionClipsInvalidFieldsSelector(
        st,
        clips.map(({ id }) => id),
      ),
    );

    const showInvalidState = !disabled && invalidFields.some((f) => f.customFieldId === field.id);
    const required = !disabled && requiredFields.some((f) => f.customFieldId === field.id);

    const handleSubmit = useCallback<Required<InlineInputProps>['onSubmit']>(
      (newValue) => {
        if (newValue === field.plainTextValue) return;
        onTextChange(newValue);
      },
      [field.plainTextValue, onTextChange],
    );

    return (
      <div className="flex w-full flex-col gap-1">
        <Label className="flex items-center justify-between font-semibold" htmlFor={field.id}>
          {field.name}
          {required && <span className={cn('text-10 uppercase', showInvalidState && 'text-red-9')}>Required</span>}
        </Label>
        <ResizableEditableTextInput
          label="Field value"
          placeholder="None"
          defaultValue={field.plainTextValue}
          onSubmit={handleSubmit}
          appearance="outline"
          disabled={disabled}
          size={size}
          className={cn('rounded-8', showInvalidState && 'ring-1 ring-red-9 focus-within:ring-0')}
          inputClassName={cn('rounded-8', showInvalidState && 'focus:!ring-1 focus:!ring-red-9 focus:!border-red-9')}
          triggerClassName={cn(showInvalidState && 'border-0', !field.plainTextValue && '!text-gray-7')}
        />
        {showDescription && field.description && <p className="text-12 text-gray-10">{field.description}</p>}
      </div>
    );
  },
);

ContentCollectionBaseCustomFieldPlainText.displayName = 'ContentCollectionBaseCustomFieldPlainText';
